import { Component, Injector, OnChanges, SimpleChanges } from '@angular/core';
import { RsfMainSBaseComponent } from '../rsf-main-s-base/rsf-main-s-base.component';
import { WFAction, WfSignature, WfSignatureResource, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { Kind, RSF, RSFAmendment, RSFBase, RSFHoldPoint, RSFStatusResource } from '../../rsf';
import { HoldpointDialogComponent } from 'src/app/controls/holdpoint-dialog/holdpoint-dialog.component';
import { utils } from 'src/app/modules/libs/utils';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';

@Component({
  selector: 'rsf-main-s4',
  templateUrl: './rsf-main-s4.component.html',
  styleUrls: ['./rsf-main-s4.component.scss']
})
export class RsfMainS4Component extends RsfMainSBaseComponent implements OnChanges {
  wfSignatures?: WfSignatureResource[];
  holdPoints?: RSFHoldPoint[];
  signatures?: WfSignature[];

  enableReorder?: boolean;
  showNoHoldpointsFound = false;

  originalHoldpoints?: RSFHoldPoint[];

  rsfProxy?: RSF | RSFAmendment | null;

  aTaskLocals?: WfTaskLocalResource[];
  oTaskLocals?: WfTaskLocalResource[];

  constructor(
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector,
  ) {
    super(injector);
  }

  /**
   * Non-prod tester tool gate: only on non-production hosts, for System Administrators, with the
   * Tester Tools master switch ON. Mirrors the RSF Section 1 auto-populate gate.
   */
  get showTestTools(): boolean {
    return !this.environmentService.isProduction() && this.isSA() && this.testerToolsService.enabled;
  }

  /**
   * Whether the red "Tester Approve" button should show on this hold-point signature: gated by
   * showTestTools, the backend's role/user-independent testSignReady (this signature's turn), the
   * section being editable, an approve action existing, and the logged-in SA NOT already having a
   * live Approve button (in which case they just use the normal one).
   */
  canTestSign(s: WfSignature): boolean {
    return (
      this.showTestTools &&
      !!s.testSignReady &&
      !this.disabled &&
      !!s.approveActionId &&
      !(s.approveAction && !s.disabled)
    );
  }

  /**
   * Signs a hold-point inspection on behalf of the appropriate role-holder via the server-side
   * TestSign endpoint (no popup). The backend SignalR refresh reloads the form.
   */
  async testSign(s: WfSignature) {
    const signed = await this.service?.confirmTestSign?.(this.rsf as RSF, s, s.approveActionId, null);
    if (!signed) this.refresh();
  }

  /**
   * Refreshes the hold-point view and uses the original RSF as the read-only source for Minor Amendments.
   * @param changes Updated component inputs.
   */
  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    // Minor Amendments do not clone hold points, so display the approved RSF collection instead.
    this.rsfProxy = this.status?.isMinorAmendment
      ? this.rsf
      : this.status?.isViewingAmendment ? this.rsfAmendment : this.rsf;
    var section = this.rsfProxy?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 4);
    this.wfSignatures = section?.wfSignatures?.filter(s => s.approveID);
    if (section && this.wfSignatures)
      section.wfSignatures = this.wfSignatures;
    this.getValues();
  }

  getValues() {
    this.wfSignatures = [];
    this.signatures = [];
    this.enableReorder = false;
    if (this.rsfProxy) {

      if (!this.isLogView) {
        this.holdPoints = this.utils.cloneDeep(this.rsfProxy.rsfHoldPoints?.filter(hp => hp.status == 1).sort((a, b) => a.number - b.number));

        const holdPoints = this.rsfProxy?.rsfHoldPoints?.filter(hp => hp.status == 1) ?? [];
        const allTaskLocals = holdPoints.flatMap(hp => hp.wfTaskLocals!).filter(tl => tl?.wfTasks?.some(t => t.enabled)) ?? [];
        this.aTaskLocals = allTaskLocals?.filter(tl => holdPoints?.map(hp => hp.id).includes(tl?.rsfAmendmentHoldPointID ?? 0));
        this.oTaskLocals = allTaskLocals?.filter(tl => holdPoints?.map(hp => hp.id).includes(tl?.rsfHoldPointID ?? 0) && !this.aTaskLocals?.map(atl => atl.rsfAmendmentHoldPointID).includes(tl.rsfAmendmentHoldPointID)) ?? [];
        this.renumberHoldpoints();
      }

      const hasActiveHoldpoints = this.holdPoints?.some(hp => hp.status == 1);

      this.showNoHoldpointsFound = !hasActiveHoldpoints;

      if (!this.isLogView)
        this.enumerateAmendments();
      this.createSignatures();

      this.originalHoldpoints = this.rsf?.rsfHoldPoints.filter(hp => hp.status == 1);
      if (this.originalHoldpoints?.length)
        this.originalHoldpoints.forEach(hp => hp.signatures = this.getHoldpointSignatures(hp));
    }
  }

  /**
   * Builds hold-point signatures for display, disables duplicate approvals, and avoids advancing workflow
   * state while Minor Amendments show approved hold points as read-only.
   */
  createSignatures() {
    if (!this.isLogView) {
      if (this.rsfProxy?.kind == Kind.RSF) {
        // Minor Amendment drafting shows approved hold points without enabling their signatures.
        this.disabled = !!this.status?.isMinorAmendment || this.rsfProxy.status != FormStatusEnum.HoldPoint;

        this.wfSignatures = this.rsf?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 4)?.wfSignatures?.filter(s => this.oTaskLocals?.map(tl => tl?.id).includes(s.id));
        this.wfSignatures?.forEach(s => {
          s.holdPointID = s.wfTaskLocal.rsfHoldPointID;
          s.amendmentHoldPointID = undefined;
        });
      }
      else { // isAmendment
        this.wfSignatures = this.rsf?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 4)?.wfSignatures?.filter(s => this.aTaskLocals?.map(tl => tl?.id).includes(s.id));
        this.wfSignatures?.forEach(s => {
          s.amendmentHoldPointID = s.wfTaskLocal.rsfAmendmentHoldPointID;
          s.holdPointID = undefined;
        });
      }

      if (this.wfSignatures && !this.isLogView)
        this.wfSignatures = this.service?.setAmendmentRequesters(this.wfSignatures);

      this.signatures = [];
      if (this.wfSignatures) {
        this.wfSignatures.forEach(t => {
          // Prevent a user from approving a second inspection on this base or amendment hold point.
          const userAlreadySigned = !t.signed && this.userSigned(t);
          t.approveEnabled = this.rsfProxy?.status == FormStatusEnum.HoldPoint && !!t.approveID && !userAlreadySigned;
          // Active forms keep approvals locked, but the signer must retain access to unapprove their own hold point.
          const activeUnapprove = this.rsfProxy?.status == FormStatusEnum.Active && t.unapproveEnabled && t.signedBy?.id == this.currentUser?.id;
          // The RSF transform overrides action flags, so duplicate-signer eligibility must also disable the control itself.
          const disable = (t.signed && t.signedBy?.id != this.currentUser?.id) || userAlreadySigned;
          const signature = WfSignatureResource.transformSignature(t, (this.disabled && !activeUnapprove) || disable, true, this.currentUser);
          this.signatures?.push(signature);
        });

        // Viewing approved hold points during Minor Amendment drafting must not recalculate RSF status.
        if (this.canModifyHoldPoints)
          this.checkStatus();
      }
    }
    // Log View
    else if (this.holdPoints?.length) {
      this.wfSignatures = [];
      const wfTaskLocals = this.holdPoints.flatMap(h => h.wfTaskLocals).filter(t => t?.wfTasks?.[0].enabled);
      wfTaskLocals.forEach(t => {
        const signature: WfSignature = {
          id: t?.id,
          disabled: true,
          amendmentId: t?.amendmentID,
          name: t?.name,
          roles: t?.wfRoleLocals?.map(rl => rl.roleID),
          roleCodes: !t?.requesterSignature ? t?.wfRoleLocals?.map(rl => rl.role?.code).sort((a, b) => utils.sort(a, b)).join('/') : undefined,
          approvedBy: this.getUserInfo(t?.wfTasks?.[0]?.signedBy?.id ?? 0),
          approved: !!t?.wfTasks?.[0].signedBy,
          approvedOn: t?.wfTasks?.[0].signedOn,
          approveAction: t?.wfActionLocals?.find(a => a.action == WFAction.Approve) && !t?.wfTasks?.[0].signedBy,
          unapproveAction: t?.wfActionLocals?.find(a => a.action == WFAction.Unapprove) && !!t?.wfTasks?.[0].signedBy,
          // amendment: t.amendmentEnabled,
          required: true,
          // taskId: t.taskID,
          // value: t.value,
          // warning: false,
          enabled: true,
          holdPointID: t?.rsfHoldPointID,
          requesterSignature: t?.requesterSignature,
          userNames: t?.requesterSignature ? this.rsfProxy?.requester?.name : undefined
        };
        // this.wfSignCount = this.signCount();
        this.signatures?.push(signature);
      });
    }
    if (this.signatures?.length) {
      this.holdPoints?.forEach(hp => {
        hp.signatures = this.getHoldpointSignatures(hp);
      });
    }
  }

  enumerateAmendments() {
    // const amendments = this.rsfProxy?.rsfAmendments?.filter(a => a.status != -1);
    // let number = 1;
    // if (amendments?.length) {
    //   amendments.map(a => {
    //     a.number = number;
    //     this.holdPoints?.filter(hp => hp.amendmentID == a.id).map(hp => hp.amendmentNumber = a.number);
    //     number++;
    //   });
    // }
  }

  checkStatus(data?: any) {
    if (!this.rsfProxy || !this.signatures) return;

    const relevantSignatures = this.signatures.filter(
      s => s.holdPointID && this.holdPoints?.some(h => h.id === s.holdPointID)
    );
    const hasUnapprovedSignatures = relevantSignatures.some(s => !s.approved);

    let status: FormStatusEnum | undefined;
    if (this.rsfProxy.status === FormStatusEnum.Active && relevantSignatures.length && hasUnapprovedSignatures) {
      status = FormStatusEnum.HoldPoint;
    } else if (
      this.rsfProxy.status === FormStatusEnum.HoldPoint &&
      (!relevantSignatures.length || !hasUnapprovedSignatures)
    ) {
      status = FormStatusEnum.Active;
    }

    if (status && status != this.rsfProxy.status) {
      const rsf: RSFStatusResource = {
        id: this.rsfProxy.id,
        status,
      };

      this.service?.putStatus(rsf).toPromise()
        .catch(error => console.error(error));
    }
  }

  /**
   * Checks whether the current user already signed a sibling inspection on this hold point.
   * @param t Inspection signature whose hold point determines the comparison scope.
   * @returns True when another inspection on the same base or amendment hold point was signed by the user.
   */
  userSigned(t: WfSignatureResource) {
    const alreadySigned = this.wfSignatures?.find(
      x => ((t.holdPointID != null && x.holdPointID == t.holdPointID) ||
        (t.amendmentHoldPointID != null && x.amendmentHoldPointID == t.amendmentHoldPointID)) &&
        x.signedBy?.id == this.getCurrentUser()?.id && x.id != t.id
    );
    return alreadySigned != null;
  }

  getHoldpointSignatures(holdPoint: RSFHoldPoint) {
    let number = 1;
    const isRSFHP = !!holdPoint.rsfid;
    var signatures: WfSignature[] = [];
    if (isRSFHP) {
      var taskLocalIDs = holdPoint.wfTaskLocals?.filter(tl => tl.wfTasks?.some(t => t.enabled)).map(tl => tl.id);
      signatures = this.signatures?.filter(x => taskLocalIDs?.includes(x.id)) ?? [];
    }
    else
      signatures = this.signatures?.filter(x => x.amendmentHoldPointID == holdPoint.id) ?? [];

    signatures?.map(s => {
      s.name = `Hold Point ${holdPoint.number} Inspection ${number}`;
      s.number = number.toString();
      number++;
    });
    return signatures;
  }

  /**
   * Opens the shared hold-point dialog when the current workflow permits modifications.
   */
  createHoldPoint() {
    // Minor Amendments cannot change the approved hold-point scope.
    if (!this.canModifyHoldPoints) return;

    const holdPoint = {
      id: 0
    } as RSFHoldPoint;
    const confirm = this.dialog.open(HoldpointDialogComponent, {
      width: '60vw',
      disableClose: true,
      data: {
        // The form discriminator lets the shared dialog apply RSF or amendment identifiers.
        formType: 'RSF',
        form: this.rsf,
        formProxy: this.rsfProxy,
        holdPoint
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data) {
        // Save pending amendment fields before the hold-point request refreshes the form.
        if (!await this.persistPendingFormChanges()) return;
        this.service?.createHoldPoint(data).toPromise().then(() => {
          this.alert.success("Hold Point Created!");
        })
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }

  /**
   * Opens an existing hold point in the shared dialog when modification is allowed.
   * @param holdPoint Existing RSF hold point to edit.
   */
  editHoldPoint(holdPoint: RSFHoldPoint) {
    // Guard direct method calls in addition to hiding the action in the template.
    if (!this.canModifyHoldPoints) return;

    const confirm = this.dialog.open(HoldpointDialogComponent, {
      width: '65vw',
      disableClose: true,
      data: {
        // The form discriminator lets the shared dialog apply RSF or amendment identifiers.
        formType: 'RSF',
        form: this.rsf,
        formProxy: this.rsfProxy,
        holdPoint
      }
    });
    confirm.afterClosed().toPromise().then(async (data: RSFHoldPoint) => {
      if (data && this.rsfProxy) {
        // Save pending amendment fields before the hold-point request refreshes the form.
        if (!await this.persistPendingFormChanges()) return;
        this.service?.updateHoldPoint(data).toPromise().then(() => {
          this.alert.success("Hold Point Saved!");
        })
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }


  /**
   * Requests deletion of an existing hold point when modification is allowed.
   * @param scfHoldPoint Hold point selected for deletion.
   */
  deleteHoldPoint(scfHoldPoint: RSFHoldPoint) {
    // Guard direct method calls in addition to hiding the action in the template.
    if (!this.canModifyHoldPoints) return;

    const confirm = this.dialog.open(YesNoDialogComponent, {
      disableClose: true,
      data: {
        message: 'Are you sure you want to Delete this Hold Point?',
        icon: 'stop'
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data && this.rsfProxy) {
        // Save pending amendment fields before the hold-point request refreshes the form.
        if (!await this.persistPendingFormChanges()) return;
        this.service?.deleteHoldPoint(scfHoldPoint).toPromise().then(() => {
          this.alert.success("Hold Point Deleted!");
        })
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }

  /**
   * Persists unsaved form fields before a hold-point operation can refresh shared RSF state.
   * Full Amendment drafting uses the amendment endpoint; standard RSF editing uses the form endpoint.
   * @returns True when there are no pending edits or the pending update succeeds.
   */
  private async persistPendingFormChanges(): Promise<boolean> {
    if (!this.isFormDirty()) {
      return true;
    }

    // Hold-point operations during Full Amendment drafting must not save the original RSF.
    const updated = this.isAmendment()
      ? await this.updateAmendment()
      : await this.update();

    if (!updated) {
      return false;
    }

    this.setFormDirty(false);
    return true;
  }

  // drop(event: CdkDragDrop<any[]>) {
  //   moveItemInArray(this.holdPoints ?? [], event.previousIndex, event.currentIndex);
  //   this.renumberHoldpoints();
  // }

  /**
   * Reorders hold points when drag operations are permitted.
   * @param event Drag-and-drop operation containing the source and target positions.
   */
  drop(event: CdkDragDrop<any>) {
    // Reordering is also a hold-point modification and requires a Full Amendment.
    if (!this.canModifyHoldPoints) return;
    if (event.previousIndex === event.currentIndex) return;

    const draggedHp = event.item.data as RSFHoldPoint;

    if (this.isHoldPointDragDisabled(draggedHp)) {
      return;
    }

    const targetHp = event.container.data[event.currentIndex];

    if (targetHp && this.isHoldPointDragDisabled(targetHp)) {
      return;
    }

    moveItemInArray(this.holdPoints ?? [], event.previousIndex, event.currentIndex);

    this.renumberHoldpoints();
  }

  /**
   * Renumber movable hold points into a contiguous sequence while preserving numbers
   * already occupied by drag-disabled hold points. Persist changed numbers when not
   * in log view, and keep the local collection sorted by the current number.
   */
  renumberHoldpoints() {
    // Minor Amendments must preserve the approved hold-point order and numbering.
    if (!this.canModifyHoldPoints) return;

    // Exit early when there are no hold points to normalize.
    if (!this.holdPoints?.length) return;

    // Reserve numbers used by drag-disabled hold points so they are never reassigned.
    const disabledNumbers = new Set(
      this.holdPoints
        .filter(h => this.isHoldPointDragDisabled(h))
        .map(h => h.number)
    );

    let nextNumber = 1;
    // Track only hold points whose number changed for selective backend update.
    const changed: RSFHoldPoint[] = [];

    for (const hp of this.holdPoints) {
      // Keep drag-disabled hold points untouched.
      if (this.isHoldPointDragDisabled(hp)) {
        continue;
      }

      // Skip any reserved number before assigning the next available value.
      while (disabledNumbers.has(nextNumber)) {
        nextNumber++;
      }

      // Apply and collect the update only when the number actually changes.
      if (hp.number !== nextNumber) {
        hp.number = nextNumber;
        changed.push(hp);
      }

      nextNumber++;
    }

    // Re-sort to keep visual and processing order aligned with current numbering.
    this.holdPoints = [...this.holdPoints].sort((a, b) => a.number - b.number);

    // Persist renumbered hold points unless the component is in read-only log view.
    if (changed.length && !this.isLogView) {
      this.service?.updateHoldPointNumbers(changed).toPromise();
    }
  }

  holdPointUnsigned(hp: RSFHoldPoint) {
    return hp.wfTaskLocals?.filter(x => x.wfTasks?.some(t => t.signedBy)).length == 0;
  }

  amendmentActive() {
    return true;
    // return this.rsfProxy?.rsfAmendments?.find(a => a.active);
  }

  /**
   * Detects Full Amendment hold-point differences that require amendment highlighting.
   * @param hp Hold point being compared with the original RSF.
   * @returns True when a Full Amendment changed the hold point.
   */
  diffHoldpoint(hp: RSFHoldPoint) {
    // Minor Amendments display the approved hold points read-only and must not highlight them as changed.
    if (this.isLogView || this.status?.isMinorAmendment || !this.status?.isViewingAmendment) return false;
    const originalHP = this.originalHoldpoints?.find(ohp => hp.number == ohp.number && hp.status == 1);

    const aTaskLocals = hp.wfTaskLocals?.filter(tl => tl.wfTasks?.[0]?.enabled);
    const oTaskLocals = originalHP?.wfTaskLocals?.filter(tl => tl.wfTasks?.[0]?.enabled && !aTaskLocals?.map(atl => atl.rsfAmendmentHoldPointID).includes(tl.rsfAmendmentHoldPointID));

    if (hp.description != originalHP?.description) return true;
    if (aTaskLocals?.length != oTaskLocals?.length) return true;

    let diff = false;
    if (oTaskLocals)
      aTaskLocals?.forEach((tl, i) => {
        const otl = oTaskLocals[i];
        if (tl.wfRoleLocals?.map(rl => rl.roleID).join('|') != otl?.wfRoleLocals?.map(rl => rl.roleID).join('|')) diff = true;
        if (tl.wfUserLocals?.map(ul => ul.userID).join('|') != otl?.wfUserLocals?.map(ul => ul.userID).join('|')) diff = true;
      });

    return diff;
  }

  /**
   * Determines whether a hold point can participate in reordering. Signed hold points remain
   * reorderable during eligible amendments, while Minor Amendments and log views remain read-only.
   * @param hp Hold point being evaluated.
   * @returns True when dragging must remain disabled.
   */
  isHoldPointDragDisabled(hp: RSFHoldPoint): boolean {
    // Signing locks hold-point content, but eligible amendments may still change its sequence.
    return !this.canModifyHoldPoints || !this.enableReorder || !!this.isLogView;
  }

  /**
   * Indicates whether the active workflow may create or modify hold points.
   * @returns False for Minor Amendments; otherwise true.
   */
  get canModifyHoldPoints(): boolean {
    return !this.status?.isMinorAmendment;
  }

  canSortHoldPoint = (
    index: number,
    drag: CdkDrag<RSFHoldPoint>,
    drop: CdkDropList<RSFHoldPoint[]>
  ): boolean => {
    const draggedHp = drag.data;
    const targetHp = drop.data[index];

    // Dragged item cannot move
    if (this.isHoldPointDragDisabled(draggedHp)) {
      return false;
    }

    // Target item cannot be displaced
    if (targetHp && this.isHoldPointDragDisabled(targetHp)) {
      return false;
    }

    return true;
  };

}
